import { ChevronDownIcon, Loader2Icon } from "lucide-react";
import { FC, useRef } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "~/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "~/components/ui/dropdown-menu";
import { useToast } from "~/hooks/use-toast";
import { useReportSampleUpload } from "../../hooks/useReportSampleUpload";
import { MAX_FILE_SIZE_BYTES } from "./importExportUtils";

interface ImportTemplateButtonProps {
  organizationId: string;
  onImportJson: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const ImportTemplateButton: FC<ImportTemplateButtonProps> = ({
  organizationId,
  onImportJson,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const pdfMarkdownInputRef = useRef<HTMLInputElement | null>(null);
  const { toast } = useToast();
  const intl = useIntl();

  const { uploadReportSample, isUploading } = useReportSampleUpload({
    organizationId,
  });

  const handleImportFromPdfMarkdown = () => {
    if (!isUploading) {
      pdfMarkdownInputRef.current?.click();
    }
  };

  const handlePdfMarkdownFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // Reset input

    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast({
        variant: "destructive",
        title: intl.formatMessage({
          id: "importTemplate.fileTooLarge",
          defaultMessage: "File size exceeds the maximum limit of 25MB",
        }),
      });
      return;
    }

    try {
      await uploadReportSample(file);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to import template",
      });
    }
  };

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json"
        className="hidden"
        onChange={onImportJson}
      />
      <input
        ref={pdfMarkdownInputRef}
        type="file"
        accept="application/pdf,.md,.markdown"
        className="hidden"
        onChange={handlePdfMarkdownFileChange}
      />
      <div className="inline-flex">
        <Button
          variant="outline"
          className="rounded-r-none border-r-0"
          onClick={() => pdfMarkdownInputRef.current?.click()}
          disabled={isUploading}
        >
          {isUploading && <Loader2Icon className="w-4 h-4 mr-2 animate-spin" />}
          <FormattedMessage
            id={isUploading ? "settings.importing" : "settings.importTemplate"}
            defaultMessage={isUploading ? "Loading..." : "Import"}
          />
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="outline"
              className="rounded-l-none px-2"
              aria-label={intl.formatMessage({
                id: "settings.importTemplate.moreOptions",
              })}
              disabled={isUploading}
            >
              <ChevronDownIcon className="w-4 h-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={handleImportFromPdfMarkdown}>
              <FormattedMessage
                id="settings.importTemplate.pdfMarkdown"
                defaultMessage="Import from PDF/Markdown"
              />
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => fileInputRef.current?.click()}>
              <FormattedMessage
                id="settings.importTemplate.json"
                defaultMessage="Import from JSON"
              />
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  );
};
